import { MapObjectProperties } from "@iventis/map-types";
import GeoJSON from "geojson";
import { Content } from "@iventis/translations/content/typed-content";
import { Body2 } from "@iventis/styles/src/components/texts";
import React, { FunctionComponent, useState, useMemo } from "react";
import { styled } from "@iventis/styles";
import { canParseFloat, thousandSeparator } from "@iventis/utilities";
import { getAreaInAppropriateUnits } from "@iventis/map-engine/src/utilities/geojson-helpers";
import area from "@turf/area";
import { convertArea } from "@turf/helpers";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";
import { NumberOnlyInput } from "@iventis/components/src/number-only-input";
import { contentCss, inputCss } from "./analysis.helpers";
import { calcCapacity, FruinServiceLevelsComponent } from "./fruin-service-levels";

export const AnalysisCapacityComponent: FunctionComponent<{ feature: GeoJSON.Feature<GeoJSON.Polygon, MapObjectProperties>; unitOfMeasurement: UnitOfMeasurement }> = ({
    feature,
    unitOfMeasurement,
}) => {
    const translate = useIventisTranslate();
    // People per metre squared
    const [ppmInputValue, setPpmInputValue] = useState("1");
    const capacity = useMemo(
        () =>
            canParseFloat(ppmInputValue)
                ? calcCapacity(convertArea(area(feature), "meters", unitOfMeasurement === UnitOfMeasurement.Imperial ? "feet" : "meters"), parseFloat(ppmInputValue))
                : 0,
        [ppmInputValue, feature]
    );

    return (
        <>
            <StyledContent>
                <Body2 className="bold">{translate(Content.map2.analysis.capacity_content.total_area)}</Body2>
                <Body2>{getAreaInAppropriateUnits(feature, unitOfMeasurement)}</Body2>
            </StyledContent>
            <StyledContent>
                <Body2 className="bold">{translate(Content.map2.analysis.capacity_title)}</Body2>
                <Body2>{thousandSeparator(capacity)}</Body2>
                <div>
                    <Body2>{translate(Content.map2.analysis.capacity_content.at)}</Body2>
                    <NumberOnlyInput value={ppmInputValue} onChange={(e) => setPpmInputValue(e.target.value)} style={inputCss} type="number" variant="outlined" />
                    <Body2>
                        {translate(
                            unitOfMeasurement === UnitOfMeasurement.Metric
                                ? Content.map2.analysis.capacity_content.people_meter_sq
                                : Content.map2.analysis.capacity_content.people_ft_sq
                        )}
                    </Body2>
                </div>
            </StyledContent>
            <StyledContent data-testid="capacity-analysis-result">
                <Body2 className="bold">{translate(Content.map2.analysis.capacity_content.fruin_service_levels)}</Body2>
                <FruinServiceLevelsComponent fruinType="capacity" magnitude={area(feature)} />
            </StyledContent>
        </>
    );
};

const StyledContent = styled.div`
    ${contentCss}
`;
